import Cookies from "@/helpers/cookies"

const COOKIE_NAME = "user_geo_location"
const EXPIRATION_IN_DAYS = 30 / (24 * 60) // 30 minutes

const setupGeolocation = async (): Promise<void> => {
  if (!Cookies.get(COOKIE_NAME)) {
    try {
      const position = await requestLocation()
      const { latitude, longitude } = position.coords
      const cookieValue = JSON.stringify({
        lat: latitude.toFixed(4),
        lng: longitude.toFixed(4)
      })

      Cookies.set(COOKIE_NAME, cookieValue, { expires: EXPIRATION_IN_DAYS })
    } catch {
      // For the passive, on-load feature, we fail silently without interrupting the user.
    }
  }
}

const requestLocation = (): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      return reject(new Error("Geolocation is not supported by this browser."))
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      // Timeout includes user's response time to the browser's permission prompt.
      timeout: 15000, // Increased timeout for better accuracy
      maximumAge: 0
    })
  })
}

const triggerGeolocationSearch = async (): Promise<{ lat: string; lng: string } | null> => {
  const locationCookie = Cookies.get(COOKIE_NAME)

  if (locationCookie) {
    try {
      return JSON.parse(locationCookie)
    } catch {
      Cookies.remove(COOKIE_NAME)

      return null
    }
  }

  try {
    const position = await requestLocation()

    const { latitude, longitude } = position.coords
    const coords = { lat: latitude.toFixed(4), lng: longitude.toFixed(4) }
    Cookies.set(COOKIE_NAME, JSON.stringify(coords), { expires: EXPIRATION_IN_DAYS })

    return coords
  } catch (error) {
    if (error instanceof GeolocationPositionError) {
      throw error
    }

    return null
  }
}

const listenToGeoPermissionChanges = (): void => {
  if (!navigator.permissions) return

  void navigator.permissions.query({ name: "geolocation" }).then((status) => {
    status.onchange = (): void => {
      if (status.state === "denied") {
        Cookies.remove(COOKIE_NAME)
      }
    }
  })
}

export { setupGeolocation, triggerGeolocationSearch, listenToGeoPermissionChanges }
